import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Play, Loader2, Eye, EyeOff, CheckCircle, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [plans, setPlans] = useState({});
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    plan: "starter"
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/payments/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Failed to fetch plans");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Account created! Welcome to SelleCast!");
      
      // Redirect to payment if not starter plan
      if (formData.plan !== "starter") {
        // Initiate payment
        const paymentResponse = await axios.post(
          `${API}/payments/checkout`,
          {
            package_type: "plan",
            package_id: formData.plan,
            origin_url: window.location.origin
          },
          {
            headers: { Authorization: `Bearer ${response.data.access_token}` }
          }
        );
        window.location.href = paymentResponse.data.url;
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const planOptions = [
    { id: "starter", name: "Starter", price: "$29/mo", desc: "100 credits" },
    { id: "professional", name: "Professional", price: "$99/mo", desc: "500 credits", popular: true },
    { id: "enterprise", name: "Enterprise", price: "$299/mo", desc: "2000 credits" }
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          </div>
          <h1 className="font-heading font-bold text-2xl text-text-primary">Create Account</h1>
          <p className="text-text-secondary mt-2">Start your SelleCast journey</p>
        </div>

        <Card className="bg-background-paper border-border">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-text-primary">Username</Label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Choose a username"
                  className="mt-1 bg-background border-border text-text-primary"
                  data-testid="username-input"
                />
              </div>

              <div>
                <Label className="text-text-primary">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                  className="mt-1 bg-background border-border text-text-primary"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-text-primary">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a password"
                    className="mt-1 bg-background border-border text-text-primary pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Select Plan</Label>
                <div className="grid grid-cols-3 gap-2">
                  {planOptions.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setFormData({ ...formData, plan: plan.id })}
                      className={`p-3 rounded-lg border cursor-pointer transition-all relative ${
                        formData.plan === plan.id
                          ? "bg-primary/10 border-primary"
                          : "bg-background border-border hover:border-primary/50"
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] px-2 py-0.5 rounded-full">
                          Popular
                        </div>
                      )}
                      <div className="text-sm font-medium text-text-primary">{plan.name}</div>
                      <div className="text-xs text-primary font-bold">{plan.price}</div>
                      <div className="text-xs text-text-muted">{plan.desc}</div>
                      {formData.plan === plan.id && (
                        <CheckCircle className="w-4 h-4 text-primary absolute top-2 right-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary-hover text-white py-6"
                data-testid="signup-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</>
                ) : formData.plan !== "starter" ? (
                  <><CreditCard className="w-4 h-4 mr-2" />Create Account & Pay</>
                ) : (
                  "Create Free Account"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-text-muted text-sm">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-text-muted text-sm hover:text-text-primary">
            ← Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
